import { useCallback, useEffect, useRef } from "react";
import type { IncidentReport, IncidentReportSectionType } from "components/CriticalIncident/interfaces";
import type { IncidentReportUpdatePayload } from "api/types";
import { useIncidentReportContext } from "../IncidentReportContextProvider";

const THROTTLE_MS = 500;

export function useThrottledSectionUpdate<S extends IncidentReportSectionType>(
  section: S,
  currentValues: IncidentReport[S],
) {
  const { updateReport } = useIncidentReportContext();
  const latestSectionValues = useRef(currentValues);
  const throttleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    latestSectionValues.current = currentValues;
  }, [currentValues]);

  return useCallback((patch?: Partial<IncidentReport[S]>) => {
    if (throttleTimerRef.current !== null) return;
    updateReport({ section, ...latestSectionValues.current, ...patch } as IncidentReportUpdatePayload);
    throttleTimerRef.current = setTimeout(() => {
      throttleTimerRef.current = null;
    }, THROTTLE_MS);
  }, [section, updateReport]);
}
